import type { ReactNode } from "react";

/**
 * F-filters-ux — componentes compartilhados para padronizar as barras de
 * filtro das listagens (antes eram fileiras `flex-wrap` poluídas de inputs de
 * larguras diferentes). Modelo de referência: página Dossiers.
 *
 *  - `FilterPanel`: card que agrupa os filtros, separado das ações da página.
 *  - `FilterField`: campo com um rótulo curto em cima (deixa escaneável).
 */

export function FilterPanel({ children, testId }: { children: ReactNode; testId?: string }) {
  // CEO 2026-10-31 — moldura igual à de Carros e Faturas (borda visível, sombra).
  return (
    <div
      className="space-y-4 rounded-xl border p-4"
      style={FILTER_PANEL_STYLE}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

export function FilterField({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`flex flex-col gap-1 max-sm:w-full ${className ?? ""}`}>
      <span
        className="text-[11px] font-semibold uppercase tracking-wider"
        style={{ color: "var(--text-body)" }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * CEO 2026-10-31 ("sem vida, texto apagado") — estilo único dos campos e
 * botões dos painéis de filtro (Carros, Faturas). Campo "rebaixado" (fundo da
 * página) com borda visível: no tema escuro não some no fundo do painel.
 */
export const FILTER_INPUT =
  "h-9 rounded-lg border border-[var(--border-strong)] bg-[var(--surface-page)] px-3 text-sm text-[var(--text-strong)] placeholder:text-[var(--text-muted)] outline-none transition-colors hover:border-[var(--text-faint)] focus:border-lime focus:ring-2 focus:ring-lime/25 disabled:opacity-50 max-sm:w-full";

const CHIP_BASE =
  "inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-[13px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime";
/** Botão desligado: neutro e legível. */
export const CHIP_OFF =
  CHIP_BASE +
  " border-[var(--border-strong)] bg-[var(--surface-muted)] text-[var(--text-body)] hover:border-lime hover:text-[var(--text-strong)]";
/** Botão ligado: verde. */
export const CHIP_ON = CHIP_BASE + " border-lime bg-lime/15 text-[var(--text-strong)]";

/** Cartão do painel de filtros (mesma moldura em Carros e Faturas). */
export const FILTER_PANEL_STYLE = {
  background: "var(--surface-card)",
  borderColor: "var(--border-strong)",
  boxShadow: "var(--shadow-sm)",
} as const;
